import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Dots,
  Eye,
  GripVertical,
  Pencil,
  Plus,
  Trash
} from 'tabler-icons-react';
import {
  ActionIcon,
  Anchor,
  Button,
  Divider,
  Image,
  Loader,
  Menu,
  Skeleton,
  TextInput,
  Textarea
} from '@mantine/core';
import PropTypes from 'prop-types';
import AppStack from '../../common/AppStack';
import ActionableIcon from '../../common/ActionableIcon';
import AppFlexbox from '../../common/AppFlexbox';
import AppText from '../../common/AppText';
import { Context as AuthContext } from '../../../providers/AuthContextProvider';
import { Context as VendorContext } from '../../../providers/VendorContextProvider';
import AppCard from '../../common/AppCard';
import { triggerNotification } from '../../../helpers/notification';
import ConfirmModal from '../../common/ConfirmModal';
import {
  useGlobalFormState,
  useMediaQueryIndex,
  useModalState
} from '../../../helpers/hooks';
import VendorProductAssignModal from './VendorProductAssignModal';
import SortableList from '../../common/SortableList';
import { VIEW_ACTIONS_ENUM } from '../../../config/constants';
import {
  getEcomVendorProductStatus,
  useVendorBackPath
} from '../../../helpers/vendorHelper';
import AppMenu from '../../common/AppMenu';
import RemoveVendorProductVariantsModal from './RemoveVendorProductVariantsModal';
import ChangeProductStatusModal from '../products/ChangeProductStatusModal';
import EditProductCard from '../products/EditProductCard';
import MediaDropzone from '../media/MediaDropzone';

const VendorCollectionEditView = ({ duplicate }) => {
  const hasFetched = useRef(false);
  const navigate = useNavigate();
  const {
    isLargeMobileOrSmaller,
    isTabletOrSmaller,
    isLaptopOrSmaller
  } = useMediaQueryIndex();
  const { pkEcomVendorCollection } = useParams();
  const { state: authState } = useContext(AuthContext);
  const {
    state,
    createVendorCollections,
    updateVendorCollection,
    deleteVendorCollections,
    fetchVendorCollection,
    fetchVendorSetupSummary
  } = useContext(VendorContext);
  const {
    state: modalState,
    onOpenModal,
    onCloseModal,
    onChangeModalLoading
  } = useModalState();
  const {
    hasInitialized,
    hasUnsavedChanges,
    formState,
    isSubmitting,
    setFormState,
    resetFormState,
    submitFormState,
    ConfirmDiscardModal
  } = useGlobalFormState(
    {
      name: '',
      description: '',
      src: '',
      fkEcomVendorMedia: null,
      ecomVendorCollectionProducts: []
    },
    { confirmDiscard: true, containerWidth: 950 }
  );
  const [expandProducts, setExpandProducts] = useState(false);

  const { backPath } = useVendorBackPath(
    duplicate
      ? `/vendor/collections/${pkEcomVendorCollection}`
      : `/vendor/collections`
  );
  const ecomVendorCollection = pkEcomVendorCollection
    ? state.ecomVendorCollection.value
    : null;
  const hasAlreadyFetched =
    hasFetched.current ||
    ecomVendorCollection?.pkEcomVendorCollection.toString() ===
      pkEcomVendorCollection;
  const loading = pkEcomVendorCollection
    ? !hasInitialized ||
      !hasAlreadyFetched ||
      state.ecomVendorCollection.loading
    : !hasInitialized;

  useEffect(() => {
    if (
      pkEcomVendorCollection &&
      ecomVendorCollection?.pkEcomVendorCollection.toString() !==
        pkEcomVendorCollection
    ) {
      fetchVendorCollection(
        pkEcomVendorCollection,
        {},
        () => {},
        (error) => triggerNotification(error)
      );
      hasFetched.current = true;
    }
  }, [pkEcomVendorCollection]);

  useEffect(() => {
    if (
      !state.ecomVendorCollection.loading &&
      (!pkEcomVendorCollection || ecomVendorCollection)
    ) {
      resetFormState({
        name: ecomVendorCollection?.name ?? '',
        description: ecomVendorCollection?.description ?? '',
        src: ecomVendorCollection?.previewImage ?? '',
        fkEcomVendorMedia: ecomVendorCollection?.fkEcomVendorMedia ?? null,
        ecomVendorCollectionProducts:
          ecomVendorCollection?.ecomVendorCollectionProducts.map((p) => ({
            key: p.ecomVendorProduct.uuid,
            uuid: p.ecomVendorProduct.uuid,
            sort: p.sort,
            name: p.ecomVendorProduct.name,
            previewImage: p.ecomVendorProduct.previewImage,
            fkEcomProductStatus: p.ecomVendorProduct.fkEcomProductStatus
          })) ?? []
      });

      setExpandProducts(
        (ecomVendorCollection?.ecomVendorCollectionProducts?.length ?? 0) <= 10
      );
    }
  }, [ecomVendorCollection, state.ecomVendorCollection.loading, duplicate]);

  const onRemoveProduct = (uuid) => {
    setFormState({
      ...formState,
      ecomVendorCollectionProducts: formState.ecomVendorCollectionProducts
        .filter((p) => p.uuid !== uuid)
        .sort((a, b) => a.sort - b.sort)
        .map((p, i) => ({ ...p, sort: i + 1 }))
    });
  };

  const onAddProducts = (products) => {
    const newEcomVendorProducts = products.filter(
      (p) =>
        !formState.ecomVendorCollectionProducts.find((f) => f.uuid === p.uuid)
    );
    setFormState({
      ...formState,
      ecomVendorCollectionProducts: [
        ...formState.ecomVendorCollectionProducts.filter((p) =>
          products.some((prod) => prod.uuid === p.uuid)
        ),
        ...newEcomVendorProducts.map((p, i) => ({
          ...p,
          sort: formState.ecomVendorCollectionProducts.length + i + 1,
          key: new Date().getTime() + i
        }))
      ]
        .sort((a, b) => a.sort - b.sort)
        .map((p, i) => ({ ...p, sort: i + 1 }))
    });
  };

  const onSaveCollection = () => {
    submitFormState((formData, onErrorCallback) => {
      if (ecomVendorCollection && !duplicate) {
        updateVendorCollection(
          ecomVendorCollection.pkEcomVendorCollection,
          {
            ...formState,
            fkEcomVendorMedia: !formState.fkEcomVendorMedia
              ? 0
              : formState.fkEcomVendorMedia
          },
          () => {
            triggerNotification('Collection updated!', 'success');
          },
          onErrorCallback
        );
      }
      else {
        createVendorCollections(
          authState.pkEcomVendor,
          { ecomVendorCollections: [formState], selectFirstItem: true },
          (data) => {
            navigate(`/vendor/collections/${data[0].pkEcomVendorCollection}`);
            triggerNotification('Collection created!', 'success');
            if (
              state.ecomVendorSetupSummary.value?.ecomVendor.sandboxEnabled &&
              !state.ecomVendorSetupSummary.value?.hasCollections
            ) {
              fetchVendorSetupSummary(authState.pkEcomVendor, {skipLoading: true});
            }
          },
          onErrorCallback
        );
      }
    });
  };

  return !loading && (!pkEcomVendorCollection || hasInitialized) ? (
    <>
      <AppStack
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onSaveCollection();
        }}
        p={{ base: '8px 0px', sm: '16px 16px 80px 16px' }}
        style={{
          flex: 1,
          margin: 'auto',
          width: '100%',
          maxWidth: 950,
          paddingTop: 16,
          gap: 16
        }}
      >
        <AppFlexbox
          p={{ base: '0px 8px', sm: 0 }}
          style={{
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%'
          }}
        >
          <AppFlexbox style={{ gap: 5, alignItems: 'center' }}>
            <ActionableIcon
              color="dark"
              component={Link}
              radius="md"
              to={backPath}
              variant="subtle"
            >
              <ArrowLeft />
            </ActionableIcon>
            <AppText style={{ fontSize: 18, fontWeight: 700 }}>
              {ecomVendorCollection?.name ?? 'Create collection'}
            </AppText>
          </AppFlexbox>

          {!!ecomVendorCollection && !duplicate && (
            <AppFlexbox style={{ gap: 5, alignItems: 'center' }}>
              <Button
                color="dark"
                component={Link}
                disabled={isSubmitting}
                radius="md"
                size="xs"
                to={`/vendor/collections/${ecomVendorCollection.pkEcomVendorCollection}/duplicate`}
                type="button"
                variant="light"
              >
                Duplicate
              </Button>
            </AppFlexbox>
          )}
        </AppFlexbox>

        {duplicate ? (
          <AppCard
            radius={isTabletOrSmaller ? 0 : 'md'}
            shadow="xs"
            style={{ padding: 8 }}
            withBorder
          >
            <AppFlexbox style={{ gap: 8 }}>
              <AppStack>
                <AlertCircle color="#eca70a" style={{ size: 18 }} />
              </AppStack>
              <AppText style={{ fontSize: 14 }}>
                Duplicate this collection by changing the <b>Title</b> and
                clicking save.
              </AppText>
            </AppFlexbox>
          </AppCard>
        ) : (
          <AppCard
            radius={isTabletOrSmaller ? 0 : 'md'}
            shadow="xs"
            style={{ padding: 8 }}
            withBorder
          >
            <AppFlexbox style={{ gap: 8 }}>
              <AppStack>
                <AlertCircle color="dodgerblue" style={{ size: 18 }} />
              </AppStack>
              <AppText style={{ fontSize: 14 }}>
                Use collections to group products together, making it easier for
                merchants to browse similar products!
              </AppText>
            </AppFlexbox>
          </AppCard>
        )}

        <AppFlexbox
          style={{ flexDirection: isLaptopOrSmaller ? 'column' : 'row' }}
        >
          <AppStack
            style={{
              flex: 2,
              gap: 16
            }}
          >
            <AppCard
              radius={isTabletOrSmaller ? 0 : 'md'}
              shadow="xs"
              style={{ padding: 16 }}
              withBorder
            >
              <AppStack>
                <TextInput
                  disabled={isSubmitting}
                  label="Title"
                  onChange={(e) =>
                    setFormState({
                      ...formState,
                      name: e.currentTarget.value
                    })
                  }
                  required
                  value={formState.name}
                />

                <Textarea
                  autosize
                  disabled={isSubmitting}
                  label="Description"
                  minRows={4}
                  onChange={(e) =>
                    setFormState({
                      ...formState,
                      description: e.currentTarget.value
                    })
                  }
                  value={formState.description}
                />
              </AppStack>
            </AppCard>

            <AppCard
              radius={isTabletOrSmaller ? 0 : 'md'}
              shadow="xs"
              style={{ padding: 0 }}
              withBorder
            >
              <AppStack style={{ gap: 0 }}>
                <AppFlexbox
                  style={{
                    padding: 16,
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <AppText style={{ fontSize: 16, fontWeight: 700 }}>
                    Products
                  </AppText>

                  <AppFlexbox style={{ gap: 8 }}>
                    {pkEcomVendorCollection &&
                      formState.ecomVendorCollectionProducts.length > 0 && (
                        <AppMenu
                          control={
                            <ActionIcon
                              color="#ced4da"
                              size="compact-sm"
                              variant="outline"
                            >
                              <Dots color="#000" />
                            </ActionIcon>
                          }
                          styles={{ dropdown: { padding: 0 } }}
                        >
                          <AppStack style={{ padding: 2, gap: 1 }}>
                            <Menu.Item
                              component={Link}
                              leftSection={<Pencil size={18} />}
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                              to={`/vendor/products/bulk-edit?collection=${pkEcomVendorCollection}`}
                              visibleFrom="xsm"
                            >
                              Bulk edit
                            </Menu.Item>
                            <Menu.Item
                              leftSection={<Eye size={18} />}
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                onOpenModal(VIEW_ACTIONS_ENUM.CHANGE_STATUS);
                              }}
                            >
                              Change status
                            </Menu.Item>

                            {authState.userData.isAdmin && (
                              <>
                                <Divider />
                                <Menu.Label>Admin actions</Menu.Label>
                                <AppStack style={{ padding: 2, gap: 1 }}>
                                  <Menu.Item
                                    leftSection={<Trash size={18} />}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      onOpenModal('REMOVE_PRODUCT_VARIANTS');
                                    }}
                                    style={{ color: 'red' }}
                                  >
                                    Remove variants
                                  </Menu.Item>
                                </AppStack>
                              </>
                            )}
                          </AppStack>
                        </AppMenu>
                      )}
                    <Button
                      color="dark"
                      disabled={isSubmitting}
                      leftSection={<Plus />}
                      onClick={() => onOpenModal(VIEW_ACTIONS_ENUM.CREATE)}
                      size="compact-sm"
                      type="button"
                      variant="filled"
                    >
                      Add
                    </Button>
                  </AppFlexbox>
                </AppFlexbox>
                <AppStack style={{ gap: 0 }}>
                  {state.ecomVendorProducts.loading ? (
                    <>
                      <Divider />
                      <AppStack
                        style={{
                          gap: 5,
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '20px 10px',
                          flex: 1
                        }}
                      >
                        <Loader color="dark" size={34} />
                      </AppStack>
                    </>
                  ) : formState.ecomVendorCollectionProducts.length === 0 ? (
                    <>
                      <Divider />
                      <AppStack
                        style={{
                          padding: 16,
                          gap: 10,
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <AppText style={{ fontWeight: 500, color: '#666' }}>
                          No products added to this collection.
                        </AppText>
                        <Button
                          color="dark"
                          disabled={isSubmitting}
                          leftSection={<Plus />}
                          onClick={() => onOpenModal(VIEW_ACTIONS_ENUM.CREATE)}
                          size="compact-sm"
                          type="button"
                          variant="outline"
                        >
                          Add product
                        </Button>
                      </AppStack>
                    </>
                  ) : (
                    <>
                      <SortableList
                        items={
                          expandProducts
                            ? formState.ecomVendorCollectionProducts
                            : [...formState.ecomVendorCollectionProducts]
                                .sort((a, b) => a.sort - b.sort)
                                .slice(0, 5)
                        }
                        onChange={(newList) => {
                          setFormState({
                            ...formState,
                            ecomVendorCollectionProducts: [
                              ...formState.ecomVendorCollectionProducts.filter(
                                (p) => !newList.find((n) => n.uuid === p.uuid)
                              ),
                              ...newList.map((p, index) => ({
                                ...p,
                                sort: index + 1
                              }))
                            ]
                          });
                        }}
                        renderItem={(
                          item,
                          {
                            attributes,
                            listeners,
                            isDragging,
                            setNodeRef,
                            style,
                            isOverlay
                          }
                        ) => {
                          const productStatusInfo = getEcomVendorProductStatus(
                            item
                          );

                          return (
                            <AppCard
                              ref={setNodeRef}
                              radius={0}
                              shadow="none"
                              style={{
                                ...style,
                                padding: 0,
                                outline: isOverlay
                                  ? 'solid 1px #dee2e6'
                                  : 'none',
                                backgroundColor: isDragging
                                  ? 'rgba(56, 56, 56, 0.1)'
                                  : '#FFF',
                                opacity: isDragging ? 0.4 : 1,
                                cursor: isDragging ? 'grabbing' : 'default'
                              }}
                            >
                              <AppFlexbox
                                style={{
                                  padding: 10,
                                  gap: 10,
                                  borderTop: isOverlay
                                    ? 'none'
                                    : 'solid 1px #dee2e6',
                                  flex: 1,
                                  alignItems: 'center'
                                }}
                              >
                                <ActionIcon
                                  color="dark"
                                  disabled={isSubmitting}
                                  variant="subtle"
                                  {...attributes}
                                  {...listeners}
                                >
                                  <GripVertical size={20} />
                                </ActionIcon>

                                <AppFlexbox
                                  style={{
                                    flex: 1,
                                    gap: 10,
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                  }}
                                >
                                  <AppFlexbox style={{ alignItems: 'center' }}>
                                    <AppStack
                                      style={{
                                        width: 40,
                                        height: 40,
                                        border: 'solid 1px #dee2e6'
                                      }}
                                    >
                                      <Image
                                        h={40}
                                        src={item.previewImage}
                                        w={40}
                                      />
                                    </AppStack>
                                    <AppStack style={{ gap: 0 }}>
                                      <Anchor
                                        onClick={() =>
                                          navigate(
                                            `/vendor/products/${item.uuid}`
                                          )
                                        }
                                        style={{
                                          fontSize: 14,
                                          fontWeight: 500,
                                          color: '#000'
                                        }}
                                      >
                                        {item.name}
                                      </Anchor>
                                      <AppText
                                        style={{
                                          fontSize: 12,
                                          color: productStatusInfo.color
                                        }}
                                      >
                                        {productStatusInfo.label}
                                      </AppText>
                                    </AppStack>
                                  </AppFlexbox>

                                  <ActionIcon
                                    color="red"
                                    disabled={isSubmitting}
                                    onClick={() => onRemoveProduct(item?.uuid)}
                                    radius="md"
                                    variant="subtle"
                                  >
                                    <Trash />
                                  </ActionIcon>
                                </AppFlexbox>
                              </AppFlexbox>
                            </AppCard>
                          );
                        }}
                      />
                      {ecomVendorCollection &&
                        formState.ecomVendorCollectionProducts.length > 10 && (
                          <>
                            <Divider />

                            <AppFlexbox
                              onClick={() => setExpandProducts(!expandProducts)}
                              style={{
                                flex: 1,
                                padding: 10,
                                gap: 5,
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#f8f9fa',
                                cursor: 'pointer'
                              }}
                            >
                              {expandProducts ? (
                                <>
                                  <AppText
                                    style={{ fontSize: 14, fontWeight: 500 }}
                                  >
                                    Show less
                                  </AppText>

                                  <ChevronUp size={16} />
                                </>
                              ) : (
                                <>
                                  <AppText
                                    style={{ fontSize: 14, fontWeight: 500 }}
                                  >
                                    Show all{' '}
                                    {
                                      formState.ecomVendorCollectionProducts
                                        .length
                                    }{' '}
                                    products
                                  </AppText>

                                  <ChevronDown size={16} />
                                </>
                              )}
                            </AppFlexbox>
                          </>
                        )}
                    </>
                  )}
                </AppStack>
              </AppStack>
            </AppCard>
          </AppStack>

          <AppStack
            style={{ flex: 1, maxWidth: isLaptopOrSmaller ? 'unset' : 300 }}
          >
            <EditProductCard
              contentProps={{ style: { padding: 0 } }}
              title="Publishing"
            >
              <AppStack style={{ gap: 0 }}>
                <AppStack style={{ padding: '0px 16px 16px 16px' }}>
                  <AppStack style={{ gap: 0 }}>
                    <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                      Catalog
                    </AppText>
                    <AppStack style={{ gap: 0 }}>
                      <AppFlexbox
                        style={{
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <AppFlexbox style={{ gap: 5, alignItems: 'center' }}>
                          <AppStack
                            style={{
                              backgroundColor: 'green',
                              width: 10,
                              height: 10,
                              borderRadius: 10
                            }}
                          />
                          <AppText style={{ fontSize: 14 }}>Public</AppText>
                        </AppFlexbox>
                      </AppFlexbox>
                    </AppStack>
                  </AppStack>
                </AppStack>
              </AppStack>
            </EditProductCard>

            <EditProductCard
              contentProps={{ style: { padding: 0 } }}
              title="Collection image"
            >
              <AppStack style={{ gap: 0 }}>
                <AppStack style={{ padding: '0px 16px 16px 16px' }}>
                  <AppStack style={{ gap: 0 }}>
                    <MediaDropzone
                      disabled={formState.loading}
                      enableMediaSelect
                      files={[]}
                      fkEcomVendor={authState.pkEcomVendor}
                      height={200}
                      maxSelectCount={1}
                      onUpload={(media) => {
                        setFormState({
                          ...formState,
                          src: media[0]?.src,
                          fkEcomVendorMedia: media[0]?.pkEcomVendorMedia
                        });
                      }}
                      selectedPreview={formState.src}
                      uploadOnDrop
                    />
                  </AppStack>
                </AppStack>
              </AppStack>
            </EditProductCard>
          </AppStack>
        </AppFlexbox>

        <AppFlexbox
          p={{ base: 16, sm: '16px 0px' }}
          style={{
            justifyContent: 'flex-end',
            flexDirection: isLargeMobileOrSmaller ? 'column-reverse' : 'row'
          }}
        >
          {!!ecomVendorCollection && !duplicate && (
            <Button
              color="red"
              disabled={isSubmitting}
              onClick={() => {
                onOpenModal(VIEW_ACTIONS_ENUM.DELETE, ecomVendorCollection);
              }}
              radius="md"
              size={isTabletOrSmaller ? 'sm' : 'compact-sm'}
              type="button"
              variant="filled"
            >
              Delete collection
            </Button>
          )}
          <Button
            color="dark"
            disabled={!hasUnsavedChanges}
            id="save-button"
            loading={isSubmitting}
            radius="md"
            size={isTabletOrSmaller ? 'sm' : 'compact-sm'}
            type="submit"
            variant="filled"
          >
            Save
          </Button>
        </AppFlexbox>
      </AppStack>
      {ConfirmDiscardModal}

      <ConfirmModal
        confirmActionColor="red"
        confirmActionText="Yes, delete collection"
        isLoading={modalState.loading}
        isOpen={
          modalState.isOpen && modalState.action === VIEW_ACTIONS_ENUM.DELETE
        }
        message={
          <AppStack style={{ gap: 10 }}>
            <AppText>
              Are you sure you want to delete <b>{modalState.item?.name}</b>?
              This action can’t be undone.
            </AppText>
          </AppStack>
        }
        onCancel={onCloseModal}
        onConfirm={() => {
          onChangeModalLoading(true);
          deleteVendorCollections(
            authState.pkEcomVendor,
            [modalState.item.pkEcomVendorCollection],
            () => {
              triggerNotification('Collection deleted!', 'success');
              navigate('/vendor/collections');
              if (
                state.ecomVendorSetupSummary.value?.ecomVendor.sandboxEnabled
              ) {
                fetchVendorSetupSummary(authState.pkEcomVendor, {skipLoading: true});
              }
            },
            (error) => {
              triggerNotification(error);
              onChangeModalLoading(false);
            }
          );
        }}
        title={`Delete ${modalState.item?.name}?`}
      />

      <VendorProductAssignModal
        isOpen={
          modalState.isOpen && modalState.action === VIEW_ACTIONS_ENUM.CREATE
        }
        onClose={onCloseModal}
        onConfirm={(products) => {
          onAddProducts(products);
          onCloseModal();
        }}
        selectedProducts={formState.ecomVendorCollectionProducts}
        showCategoryFilter
        showDesignFilter
      />

      <RemoveVendorProductVariantsModal
        filter={{ fkEcomVendorCollection: pkEcomVendorCollection }}
        isOpen={
          modalState.isOpen && modalState.action === 'REMOVE_PRODUCT_VARIANTS'
        }
        onClose={onCloseModal}
      />

      <ChangeProductStatusModal
        fkEcomVendor={authState.pkEcomVendor}
        isOpen={
          modalState.isOpen &&
          modalState.action === VIEW_ACTIONS_ENUM.CHANGE_STATUS
        }
        onClose={onCloseModal}
        onSuccess={() => {
          fetchVendorCollection(
            pkEcomVendorCollection,
            { skipLoading: true },
            () => {},
            (error) => triggerNotification(error)
          );
          onCloseModal();
        }}
        selectAllDisabled
        selectedProductIds={formState.ecomVendorCollectionProducts.map(
          (p) => p.uuid
        )}
      />
    </>
  ) : (
    <AppStack
      p={{ base: '8px 0px', sm: '16px 16px 80px 16px' }}
      style={{
        flex: 1,
        margin: 'auto',
        width: '100%',
        maxWidth: 950,
        paddingTop: 16,
        gap: 16
      }}
    >
      <AppFlexbox
        p={{ base: '0px 8px', sm: 0 }}
        style={{
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%'
        }}
      >
        <AppFlexbox style={{ gap: 5 }}>
          <Skeleton height={30} width={200} />
        </AppFlexbox>

        {!!ecomVendorCollection && !duplicate && (
          <AppFlexbox style={{ gap: 5 }}>
            <Skeleton height={30} width={125} />
          </AppFlexbox>
        )}
      </AppFlexbox>

      <AppStack style={{ gap: 16 }}>
        <AppCard
          radius={isTabletOrSmaller ? 0 : 'md'}
          shadow="xs"
          style={{ padding: 16 }}
          withBorder
        >
          <AppStack style={{ gap: 10 }}>
            <Skeleton height={10} width="100%" />
            <Skeleton height={10} width="100%" />
            <Skeleton height={10} width="50%" />
          </AppStack>
        </AppCard>
        <AppCard
          radius={isTabletOrSmaller ? 0 : 'md'}
          shadow="xs"
          style={{ padding: 16 }}
          withBorder
        >
          <AppStack style={{ gap: 10 }}>
            <Skeleton height={10} width="100%" />
            <Skeleton height={10} width="100%" />
            <Skeleton height={10} width="50%" />
          </AppStack>
        </AppCard>
        <AppCard
          radius={isTabletOrSmaller ? 0 : 'md'}
          shadow="xs"
          style={{ padding: 16 }}
          withBorder
        >
          <AppStack style={{ gap: 10 }}>
            <Skeleton height={10} width="100%" />
            <Skeleton height={10} width="100%" />
            <Skeleton height={10} width="50%" />
          </AppStack>
        </AppCard>
      </AppStack>
    </AppStack>
  );
};

VendorCollectionEditView.propTypes = { duplicate: PropTypes.bool };

export default VendorCollectionEditView;
